/**
 * Time Formatting Utilities
 * Centralized functions for formatting durations and timestamps
 */

/**
 * Format seconds into a compact time string (e.g., "45s", "2:30")
 * @param {number} seconds - Total seconds
 * @returns {string} Formatted time string
 */
export function formatTime(seconds) {
  const safeSeconds = Math.max(0, Math.ceil(seconds))

  if (safeSeconds < 60) {
    return `${safeSeconds}s`
  }

  const mins = Math.floor(safeSeconds / 60)
  const secs = safeSeconds % 60

  if (mins < 60) {
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const hours = Math.floor(mins / 60)
  const remainingMins = mins % 60
  return `${hours}:${remainingMins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

/**
 * Format seconds into a short readable string (e.g., "2m 30s")
 * @param {number} seconds - Total seconds
 * @returns {string} Formatted time string
 */
export function formatTimeShort(seconds) {
  const safeSeconds = Math.max(0, Math.ceil(seconds))

  if (safeSeconds < 60) {
    return `${safeSeconds}s`
  }

  const mins = Math.floor(safeSeconds / 60)
  const secs = safeSeconds % 60

  if (mins < 60) {
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
  }

  const hours = Math.floor(mins / 60)
  const remainingMins = mins % 60

  if (hours < 24) {
    return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`
  }

  const days = Math.floor(hours / 24)
  const remainingHours = hours % 24
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
}

/**
 * Format seconds into a verbose readable string (e.g., "2 minutes 30 seconds")
 * @param {number} seconds - Total seconds
 * @returns {string} Formatted time string
 */
export function formatTimeLong(seconds) {
  const safeSeconds = Math.max(0, Math.ceil(seconds))

  if (safeSeconds < 60) {
    return safeSeconds === 1 ? '1 second' : `${safeSeconds} seconds`
  }

  const mins = Math.floor(safeSeconds / 60)
  const secs = safeSeconds % 60

  if (mins < 60) {
    const minText = mins === 1 ? '1 minute' : `${mins} minutes`
    if (secs === 0) return minText
    const secText = secs === 1 ? '1 second' : `${secs} seconds`
    return `${minText} ${secText}`
  }

  const hours = Math.floor(mins / 60)
  const remainingMins = mins % 60
  const hourText = hours === 1 ? '1 hour' : `${hours} hours`
  if (remainingMins === 0) return hourText
  const minText = remainingMins === 1 ? '1 minute' : `${remainingMins} minutes`
  return `${hourText} ${minText}`
}

/**
 * Format a Date or timestamp into relative time (e.g., "2 hours ago")
 * @param {Date|number|string} date - Date object, timestamp, or ISO string
 * @returns {string} Relative time string
 */
export function formatRelativeTime(date) {
  const now = new Date()
  const target = new Date(date)
  const diffMs = now - target
  const diffSeconds = Math.floor(diffMs / 1000)

  if (diffSeconds < 60) {
    return 'just now'
  }

  const diffMinutes = Math.floor(diffSeconds / 60)
  if (diffMinutes < 60) {
    return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`
  }

  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) {
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`
  }

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) {
    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`
  }

  const diffWeeks = Math.floor(diffDays / 7)
  return diffWeeks === 1 ? '1 week ago' : `${diffWeeks} weeks ago`
}

/**
 * Format milliseconds into seconds with decimal
 * @param {number} ms - Milliseconds
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted seconds string
 */
export function formatMilliseconds(ms, decimals = 1) {
  return (ms / 1000).toFixed(decimals) + 's'
}
